import filter from 'lodash-es/filter';
import React, { useEffect, useState } from 'react';
import { matchPath, useLocation } from 'react-router';
import iconNotes from '../../assets/icons/ic_notes.svg';
import iconLogout from '../../assets/icons/icons_authentication_security_24_logout.svg';
import iconLanguage from '../../assets/icons/language.svg';
import { getCookiePolicyPath, getPrivacyPolicyPath } from '../../constants/routes';
import i18n from '../../utils/i18n';
import CheckboxListItemView, { CheckboxItemProps } from '../CheckboxListItemView';
import Popover from '../Popover';
import { SidebarMenuItem, SideBarMenuProps } from './SidebarMenuItem';

const Sidebar: React.FC<SideBarMenuProps> = ({
  menu, clickHandler, headerTitle, icon,
  onClickLogout, onSelectedMenuItemIndex,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showChangeLanguagePopup, setShowChangeLanguagePopup] = useState(false);
  const currentLanguage = localStorage.getItem('i18nextLng');
  const location = useLocation();
  const [languageList] = useState([
    { label: i18n.t('bahasa.indonesia'), checked: currentLanguage === 'id', key: 'id' },
    { label: i18n.t('english'), checked: currentLanguage === 'en', key: 'en' }]);

  useEffect(() => {
    let index = menu.findIndex((i) => {
      const isRoute = matchPath(location.pathname, { path: i.route })?.isExact;
      return isRoute;
    });

    const selectedMenu = menu[index];
    if (selectedMenu && selectedMenu.parentRoute) {
      index = menu.findIndex((i) => {
        const isRoute = matchPath(selectedMenu.parentRoute, { path: i.route })?.isExact;
        return isRoute;
      });
    }
    setSelectedIndex(index);
    onSelectedMenuItemIndex(index);
  }, [location.pathname]);

  function setActiveTab(item: SidebarMenuItem) {
    setSelectedIndex(menu.indexOf(item));
    onSelectedMenuItemIndex(menu.indexOf(item));
    if (clickHandler) clickHandler(item);
  }

  function doHideChangeLanguagePopup() {
    setShowChangeLanguagePopup(false);
  }

  function handleCheckedOptions(checkedItems: CheckboxItemProps[]) {
    const dumbData = [{ label: '', checked: true }];
    const data = filter(checkedItems, { checked: true }) || dumbData;
    const selectedLanguage = data[0]?.key;

    // Update HTML lang attribute
    if (selectedLanguage) {
      document.documentElement.lang = selectedLanguage;
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    i18n.changeLanguage(selectedLanguage, () => {
      window.location.reload();
    });
  }

  const changeAppsLanguage = () => {
    setShowChangeLanguagePopup(true);
  };

  function layoutChangeLanguageContent() {
    return (
      <div className="w-[200px] text-left">
        <CheckboxListItemView
          type="single"
          isButtonNeeded={false}
          mode="SECONDARY"
          actionWhenChecked
          isClearedOnceClicked
          buttonLabel=""
          title=""
          onClickAction={(checkedItems) => {
            handleCheckedOptions(checkedItems);
          }}
          items={languageList}
        />
      </div>
    );
  }

  return (
    <div>
      <aside className="flex flex-col w-[227px] bg-zinc-50 fixed left-0 top-0" aria-label="Sidebar">
        <div className="h-screen border-solid border-r border-gray-200 bg-gray-50">
          <a href="/" className="flex px-3 py-6 items-center pl-5 mb-2">
            {icon ? (<img src={icon} alt={icon} className="rounded-lg" />) : null}
            {
              headerTitle ? (
                <span
                  className="ml-2 self-center text-lg font-semibold whitespace-nowrap"
                >
                  {headerTitle}
                </span>
              ) : null
            }
          </a>
          <ul className="space-y">
            {menu.map((item, i) => Boolean(!item.parentRoute) && (
              <li className="cursor-pointer" key={item.text + i.toString()}>
                <div
                  onClick={() => {
                    setActiveTab(item);
                  }}
                  aria-hidden="true"
                  className="flex items-center text-base font-normal text-gray-900 rounded-lg"
                >
                  <div className={`relative w-full p-5 mx-4 flex ${selectedIndex === i ? 'rounded-2xl bg-white drop-shadow' : ''}`}>
                    <img
                      src={item.icon}
                      alt="Icon Dashboard"
                      className="fill-red-900 flex-shrink-0 w-5 h-5 transition duration-75 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white"
                    />
                    <span className={`flex-1 ml-3 text-sm font-bold ${selectedIndex === i ? 'text-gojek-green' : ''}`}>{item.text}</span>

                    {item.style === 'premium' && (
                    <span
                      className="absolute right-0 ml-3 mr-3 inline-flex justify-center items-center px-2 ml-3 text-sm font-medium text-gray-800 bg-gray-200 rounded-full dark:bg-gray-700 dark:text-gray-300"
                    >
                      Pro
                    </span>
                    )}

                    {item.style === 'notification' && (
                    <span
                      className="absolute right-0 ml-6 mr-3 inline-flex justify-center items-center p-3 w-3 h-3 text-sm font-medium text-blue-600 bg-blue-200 rounded-full dark:bg-blue-900 dark:text-blue-200"
                    >
                      3
                    </span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <div className="absolute bottom-0">
            <Popover
              isOpen={showChangeLanguagePopup}
              x={20}
              y={-85}
              content={layoutChangeLanguageContent()}
              onClickOutside={() => doHideChangeLanguagePopup()}
              contentClassName="h-[150px]"
            />
            <div className=" pb-4 w-[227px] cursor-pointer">
              <div aria-hidden="true" className="flex flex-row items-center pl-8" onClick={() => { changeAppsLanguage(); }}>
                <img src={iconLanguage} alt={iconLanguage} />
                <div className="pl-4 text-sm font-bold">{i18n.t('sidebar.change.language')}</div>
              </div>
            </div>
            <div className=" pb-4 w-[227px] cursor-pointer">
              <div aria-hidden="true" className="flex flex-row items-center pl-8" onClick={() => (window.open((getCookiePolicyPath(i18n.language)), '_blank'))}>
                <img src={iconNotes} alt={iconNotes} />
                <div className="pl-4 text-sm font-bold">{i18n.t('sidebar.cookiePolicy')}</div>
              </div>
            </div>

            <div className=" pb-4 w-[227px] cursor-pointer">
              <div aria-hidden="true" className="flex flex-row items-center pl-8" onClick={() => (window.open((getPrivacyPolicyPath(i18n.language)), '_blank'))}>
                <img src={iconNotes} alt={iconNotes} />
                <div className="pl-4 text-sm font-bold">{i18n.t('sidebar.privacyPolicy')}</div>
              </div>
            </div>

            {onClickLogout && (
            <div className="pb-4 w-[227px] cursor-pointer">
              <div aria-hidden="true" className="flex flex-row items-center pl-8" onClick={() => (onClickLogout ? onClickLogout() : 0)}>
                <img src={iconLogout} alt={iconLogout} />
                <div className="pl-4 text-sm font-bold">{i18n.t('sidebar.backToHome')}</div>
              </div>
            </div>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
};

export default Sidebar;
